import { useCallback, useEffect, useRef, useState } from 'react';
import type { SetURLSearchParams } from 'react-router-dom';

import { apiService } from '../services/api';
import type { DashboardSearchContext } from '../utils/dashboardSearchParams';
import {
  buildFocusRelaxContext,
  findChangeListPageForContext,
  formatChangeFocusLabel,
  FOCUS_CHANGE_SEARCH_PARAM,
  scrollToChangeRow,
  scrollToChangesTableSection,
} from '../utils/changeTableFocus';

export interface OutsideFiltersFocusBanner {
  changeId: number;
  label: string;
}

export interface UseChangesTableFocusOptions {
  searchParams: URLSearchParams;
  setSearchParams: SetURLSearchParams;
  /** Backup quando o query param não dispara (ex.: mesma rota). */
  locationFocusChangeId?: number | null;
  onLocationFocusConsumed?: () => void;
  /** Contexto atual da tabela (filtros do usuário, sem override de foco). */
  getTableSearchContext: () => DashboardSearchContext;
  publicationDateMin: string | null;
  sortBy: string;
  sortOrder: string;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  getTableRows: () => Array<{ id: number }>;
  /** Muda quando a página de dados da tabela atualiza (dispara scroll). */
  tableRowsSignature: string;
  page: number;
}

export interface UseChangesTableFocusResult {
  highlightedChangeId: number | null;
  outsideFiltersBanner: OutsideFiltersFocusBanner | null;
  /** Substitui temporariamente o contexto da tabela após "Mostrar na lista". */
  focusContextOverride: DashboardSearchContext | null;
  clearFocusHighlight: () => void;
  showChangeInList: () => Promise<void>;
  isResolvingFocus: boolean;
}

/**
 * Foco de linha na tabela de mudanças (sininho, deep links).
 * Plano B: mantém filtros; se a norma estiver oculta, exibe banner com "Mostrar na lista".
 */
export function useChangesTableFocus({
  searchParams,
  setSearchParams,
  locationFocusChangeId,
  onLocationFocusConsumed,
  getTableSearchContext,
  publicationDateMin,
  sortBy,
  sortOrder,
  rowsPerPage,
  onPageChange,
  getTableRows,
  tableRowsSignature,
  page,
}: UseChangesTableFocusOptions): UseChangesTableFocusResult {
  const [highlightedChangeId, setHighlightedChangeId] = useState<number | null>(null);
  const [pendingFocusChangeId, setPendingFocusChangeId] = useState<number | null>(null);
  const [outsideFiltersBanner, setOutsideFiltersBanner] = useState<OutsideFiltersFocusBanner | null>(null);
  const [focusContextOverride, setFocusContextOverride] = useState<DashboardSearchContext | null>(null);
  const [isResolvingFocus, setIsResolvingFocus] = useState(false);
  const [isShowingInList, setIsShowingInList] = useState(false);
  const consumedLocationFocusRef = useRef<number | null>(null);

  const clearFocusHighlight = useCallback(() => {
    setHighlightedChangeId(null);
    setOutsideFiltersBanner(null);
    setFocusContextOverride(null);
  }, []);

  const resetFocusState = useCallback(() => {
    setHighlightedChangeId(null);
    setOutsideFiltersBanner(null);
    setFocusContextOverride(null);
  }, []);

  useEffect(() => {
    const raw = searchParams.get(FOCUS_CHANGE_SEARCH_PARAM);
    const fromUrl = raw ? Number(raw) : NaN;
    let focusId: number | null = null;

    if (Number.isFinite(fromUrl) && fromUrl > 0) {
      focusId = fromUrl;
    } else if (
      locationFocusChangeId != null &&
      locationFocusChangeId > 0 &&
      consumedLocationFocusRef.current !== locationFocusChangeId
    ) {
      focusId = locationFocusChangeId;
      consumedLocationFocusRef.current = locationFocusChangeId;
      onLocationFocusConsumed?.();
    }

    if (focusId == null) return;

    resetFocusState();
    setPendingFocusChangeId(focusId);

    if (raw) {
      const next = new URLSearchParams(searchParams);
      next.delete(FOCUS_CHANGE_SEARCH_PARAM);
      setSearchParams(next, { replace: true });
    }
  }, [
    searchParams,
    setSearchParams,
    locationFocusChangeId,
    onLocationFocusConsumed,
    resetFocusState,
  ]);

  useEffect(() => {
    if (pendingFocusChangeId == null) return;
    const changeId = pendingFocusChangeId;
    let cancelled = false;
    setIsResolvingFocus(true);

    void (async () => {
      try {
        const change = await apiService.getChangeById(changeId);
        if (cancelled) return;

        const label = formatChangeFocusLabel(change as { id: number; law?: string; title?: string });
        const ctx = getTableSearchContext();
        const foundPage = await findChangeListPageForContext(
          changeId,
          ctx,
          sortBy,
          sortOrder,
          rowsPerPage,
        );

        if (cancelled) return;

        if (foundPage != null) {
          onPageChange(foundPage);
          setHighlightedChangeId(changeId);
          setOutsideFiltersBanner(null);
        } else {
          setOutsideFiltersBanner({ changeId, label });
        }
      } catch {
        if (!cancelled) {
          setOutsideFiltersBanner(null);
        }
      } finally {
        if (!cancelled) {
          setPendingFocusChangeId(null);
          setIsResolvingFocus(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    pendingFocusChangeId,
    getTableSearchContext,
    sortBy,
    sortOrder,
    rowsPerPage,
    onPageChange,
  ]);

  const showChangeInList = useCallback(async () => {
    if (!outsideFiltersBanner) return;
    const changeId = outsideFiltersBanner.changeId;
    setIsShowingInList(true);
    try {
      const relaxedCtx = buildFocusRelaxContext(publicationDateMin);
      setFocusContextOverride(relaxedCtx);

      const foundPage = await findChangeListPageForContext(
        changeId,
        relaxedCtx,
        sortBy,
        sortOrder,
        rowsPerPage,
      );

      if (foundPage != null) {
        onPageChange(foundPage);
      }
      setHighlightedChangeId(changeId);
      setOutsideFiltersBanner(null);
    } finally {
      setIsShowingInList(false);
    }
  }, [
    outsideFiltersBanner,
    publicationDateMin,
    sortBy,
    sortOrder,
    rowsPerPage,
    onPageChange,
  ]);

  useEffect(() => {
    if (highlightedChangeId == null) return;
    const rows = getTableRows();
    if (!rows.some((row) => row.id === highlightedChangeId)) return;

    const timer = window.setTimeout(() => {
      scrollToChangesTableSection();
      scrollToChangeRow(highlightedChangeId);
    }, 150);

    return () => clearTimeout(timer);
  }, [highlightedChangeId, getTableRows, page, tableRowsSignature]);

  useEffect(() => {
    if (!outsideFiltersBanner || pendingFocusChangeId != null) return;
    const timer = window.setTimeout(scrollToChangesTableSection, 150);
    return () => clearTimeout(timer);
  }, [outsideFiltersBanner, pendingFocusChangeId]);

  return {
    highlightedChangeId,
    outsideFiltersBanner,
    focusContextOverride,
    clearFocusHighlight,
    showChangeInList,
    isResolvingFocus: isResolvingFocus || isShowingInList,
  };
}
